import { zodResolver } from '@hookform/resolvers/zod';
import { postOrder, updateOrder } from 'api/accessexpressv3/api';
import Loader from 'components/Loader';
import { Modal } from 'components/Modal/Modal';
import showToast from 'components/Toast/Toast';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { useAuthUser } from 'hooks/useAuthUser';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { buildItemPayload } from 'screens/AccessExpressV3/utils';
import { z } from 'zod';

export const SaveAsDraftFormSchema = z.object({
  order_name: z.string().trim().nonempty('Required'),
});

export type SaveAsDraftForm = z.infer<typeof SaveAsDraftFormSchema>;

const SaveAsDraft = ({
  size = 'medium',
  variant = 'primary',
  onBeforeSubmit,
  onSettled,
}: {
  size?: React.ComponentProps<typeof GraniteButton>['size'];
  variant?: React.ComponentProps<typeof GraniteButton>['variant'];
  onBeforeSubmit?: () => void;
  onSettled?: () => void;
}) => {
  const { open, close, isOpen } = useModal<boolean>();
  const cart = useCartContext();
  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(
    async ({ order_name }: { order_name: string }) => {
      onBeforeSubmit?.();

      if (cart.draftId) {
        await updateOrder({ order_name }, cart.draftId);
      } else {
        const dataForPostOrUpdate = {
          additional_details: cart.additionalDetails ?? {},
          locations: cart.locations.map((location) => {
            return {
              address: {
                ...location,
                id: location.id as string,
              },
              order_items: cart
                .itemsByLocation(location.id)
                .filter((item) => item.category === 'Access')
                .map(buildItemPayload),
            };
          }),
        };
        await postOrder({
          order_name,
          ...dataForPostOrUpdate,
          customer_name: userInfo.companyName,
          requester: `${userInfo.first_name} ${userInfo.last_name}`,
          status: 'draft',
        });
      }
    },
    {
      onSuccess: () => {
        navigate('/access-express/v3/draft');
      },
      onError: (error: { message: string }) => {
        showToast.error({
          message: error.message || 'Something went wrong!',
        });
      },
      ...(onSettled ? { onSettled } : {}),
    },
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<SaveAsDraftForm>({
    resolver: zodResolver(SaveAsDraftFormSchema),
  });
  const userInfo = useAuthUser();

  const onSubmit = (data: { order_name: string }) => {
    mutate(data);
  };

  if (cart.resumeOrder?.id) return null;

  return (
    <>
      <GraniteButton
        variant={variant}
        size={size}
        onClick={open}
        className="w-full"
        disabled={cart.totalItems === 0 && cart.locations.length === 0}
      >
        Save as draft
      </GraniteButton>
      <Modal isVisible={isOpen} close={close} className="max-w-[624px]">
        <div className="overflow-hidden rounded-lg">
          <div className="bg-background-base-surface-3 px-8 py-6">
            <h1 className="text-content-base-default">Save as draft</h1>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-12 bg-background-base-surface-2 p-8"
          >
            <GraniteInput
              {...register('order_name')}
              label="Give this quote request a name so that you can reference it later"
              error={errors.order_name?.message}
            />
            <div className="flex gap-5">
              <GraniteButton
                size="large"
                variant="secondary"
                onClick={() => close()}
              >
                Cancel
              </GraniteButton>
              <GraniteButton
                size="large"
                variant="primary"
                type="submit"
                disabled={isLoading}
              >
                Save as draft {isLoading && <Loader width="w-4" height="h-4" />}
              </GraniteButton>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default SaveAsDraft;
